import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchAllContactGroups,
  deleteContactGroup,
  updateContactGroup,
  addContactGroup,
} from '@store/actions'
import { IContactGroup } from '@dataTypes/contact.type'

type ContactGroupState = {
  data: IContactGroup[]
  loading: boolean
  error: string | null
  successMessage: string | null
}

const initialState: ContactGroupState = {
  data: [],
  loading: false,
  error: null,
  successMessage: null,
}

const contactGroupSlice = createSlice({
  name: 'contact_group',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllContactGroups.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAllContactGroups.fulfilled,
        (state, action: PayloadAction<IContactGroup[]>) => {
          state.data = action.payload
          state.loading = false
          state.successMessage = 'Contact groups loaded successfully'
        },
      )
      .addCase(fetchAllContactGroups.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    builder
      .addCase(addContactGroup.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addContactGroup.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(addContactGroup.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    builder
      .addCase(updateContactGroup.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        updateContactGroup.fulfilled,
        (state, action: PayloadAction<IContactGroup>) => {
          const index = state.data.findIndex(
            (patient) =>
              patient.contact_group_id === action.payload.contact_group_id,
          )
          if (index !== -1) {
            state.data[index] = {
              ...state.data[index],
              ...action.payload,
            }
          }
          state.loading = false
          state.successMessage = 'Contact group updated successfully'
        },
      )
      .addCase(updateContactGroup.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload ||
          'Failed to update contact group') as string
      })

    builder
      .addCase(deleteContactGroup.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteContactGroup.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false
          state.data = state.data.filter(
            (contact) => contact.contact_group_id !== action.payload,
          )
          state.successMessage = 'Contact group removed successfully'
          state.error = null
        },
      )
      .addCase(deleteContactGroup.rejected, (state) => {
        state.loading = false
        state.error = 'Failed to delete contact group'
      })
  },
})

export default contactGroupSlice.reducer
